import {
  CSSProperties,
  ReactNode,
  RefObject,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styles from "./Popover.module.css";

export type PopoverPlacement =
  | "bottom-start"
  | "bottom-end"
  | "top-start"
  | "top-end";

interface Props {
  open: boolean;
  onClose: () => void;
  anchorRef: RefObject<HTMLElement | null>;
  /**
   * PREFERRED placement relative to anchor. Default 'bottom-start'. The side
   * flips to the opposite one when the panel would not fit on this side.
   */
  placement?: PopoverPlacement;
  /** Offset in pixels from the anchor edge. Default 6. */
  offset?: number;
  children: ReactNode;
  /** Optional max-width / width override */
  width?: number | string;
  /** Optional className passed through */
  className?: string;
}

interface Position {
  top: number;
  left: number;
}

const OPPOSITE: Record<PopoverPlacement, PopoverPlacement> = {
  "bottom-start": "top-start",
  "bottom-end": "top-end",
  "top-start": "bottom-start",
  "top-end": "bottom-end",
};

function fitsInViewport(
  anchorRect: DOMRect,
  panelHeight: number,
  placement: PopoverPlacement,
  offset: number,
  viewportHeight: number,
): boolean {
  return placement.startsWith("bottom")
    ? anchorRect.bottom + offset + panelHeight <= viewportHeight
    : anchorRect.top - offset - panelHeight >= 0;
}

/**
 * The asked-for side, unless the panel does not fit there and does fit on the
 * other one. A menu that hangs off the bottom of the window is unreadable in
 * every caller, so the flip is the behaviour rather than an opt-in; when
 * neither side has room the preference wins, because at that point both
 * choices clip and the caller's is the less surprising one.
 */
function resolvePlacement(
  anchorRect: DOMRect,
  panelHeight: number,
  placement: PopoverPlacement,
  offset: number,
  viewportHeight: number,
): PopoverPlacement {
  const fits = (side: PopoverPlacement) =>
    fitsInViewport(anchorRect, panelHeight, side, offset, viewportHeight);
  const opposite = OPPOSITE[placement];
  return fits(placement) || !fits(opposite) ? placement : opposite;
}

function computePosition(
  anchorRect: DOMRect,
  panelRect: { width: number; height: number },
  placement: PopoverPlacement,
  offset: number,
): Position {
  switch (placement) {
    case "bottom-start":
      return {
        top: anchorRect.bottom + offset,
        left: anchorRect.left,
      };
    case "bottom-end":
      return {
        top: anchorRect.bottom + offset,
        left: anchorRect.right - panelRect.width,
      };
    case "top-start":
      return {
        top: anchorRect.top - offset - panelRect.height,
        left: anchorRect.left,
      };
    case "top-end":
      return {
        top: anchorRect.top - offset - panelRect.height,
        left: anchorRect.right - panelRect.width,
      };
  }
}

/**
 * A closed popover renders nothing and, more to the point, mounts nothing: the
 * panel below owns the measured position, so closing throws that position away
 * instead of having to reset it.
 */
export function Popover({ open, ...rest }: Props) {
  if (!open) return null;
  return <PopoverPanel {...rest} />;
}

function PopoverPanel({
  onClose,
  anchorRef,
  placement = "bottom-start",
  offset = 6,
  children,
  width,
  className,
}: Omit<Props, "open">) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<Position | null>(null);

  // Position calculation (after layout). `children` is a dependency because
  // the side depends on how tall the panel is, and a panel whose content
  // arrives late — search hits after the request — is a different height than
  // the one that opened. Re-measuring on every render of the caller is cheap;
  // the position it yields is usually the one already set, and an unchanged
  // position is returned as the same object so it costs no render.
  useLayoutEffect(() => {
    function recompute() {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const aRect = anchor.getBoundingClientRect();
      const pRect = {
        width: panel.offsetWidth,
        height: panel.offsetHeight,
      };
      const side = resolvePlacement(
        aRect,
        pRect.height,
        placement,
        offset,
        window.innerHeight,
      );
      const next = computePosition(aRect, pRect, side, offset);
      setPos((prev) =>
        prev && prev.top === next.top && prev.left === next.left ? prev : next,
      );
    }
    recompute();
    window.addEventListener("resize", recompute);
    window.addEventListener("scroll", recompute, true);
    return () => {
      window.removeEventListener("resize", recompute);
      window.removeEventListener("scroll", recompute, true);
    };
  }, [placement, offset, anchorRef, children]);

  // Outside click + Escape.
  useEffect(() => {
    function onDown(e: MouseEvent) {
      const target = e.target as Node | null;
      if (!target) return;
      const panel = panelRef.current;
      const anchor = anchorRef.current;
      if (panel && panel.contains(target)) return;
      if (anchor && anchor.contains(target)) return;
      onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        onClose();
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose, anchorRef]);

  const style: CSSProperties = {
    position: "fixed",
    top: pos?.top ?? -9999,
    left: pos?.left ?? -9999,
    // Hide until measured to avoid flash at (0,0).
    visibility: pos ? "visible" : "hidden",
  };
  if (width !== undefined) {
    style.width = typeof width === "number" ? `${width}px` : width;
  }

  return createPortal(
    <div
      ref={panelRef}
      className={`${styles.panel}${className ? ` ${className}` : ""}`}
      style={style}
      role="presentation"
    >
      {children}
    </div>,
    document.body,
  );
}
